import { useEffect, useState } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { api, ApiError, type MeResponse } from "../lib/api";
import { buildSiweMessage } from "../lib/siwe";
import { chainId } from "../lib/wallet";
import { flushReferralIntent } from "../lib/referral-intent";
import { cn } from "./cn";
import { useI18n } from "../i18n";

const SIWE_DOMAIN = String(window.location.hostname);
const SIWE_URI = String(window.location.origin);

/**
 * AuthRoute — SIWE / EIP-1271 / EIP-6492 auth screen.
 *
 * WO#1 §32 & §21: this preserves the existing Phase 0 SIWE flow
 * verbatim. Only the visual shell is refreshed against the Obsidian
 * tokens. No auth business logic (nonce → sign → verify → /me) is
 * modified. Data-testid attributes are preserved so existing tests
 * continue to pass.
 */
export function AuthRoute() {
  const { address, isConnected, chain: activeChain } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { t } = useI18n();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(true);
  const [status, setStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    void refreshMe();
  }, []);

  async function refreshMe() {
    setMeLoading(true);
    try {
      const m = await api.me();
      setMe(m);
      // Macro #22: once authenticated, submit any pending ?ref= intent to the
      // server-authoritative attribution endpoint. Idempotent + safe.
      if (m?.wallet) void flushReferralIntent();
    } catch {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  }

  async function handleSignIn() {
    setError("");
    setStatus(t("auth.status.requestingNonce"));
    try {
      if (!address) throw new Error("No wallet address");
      const { nonce } = await api.nonce();
      const message = buildSiweMessage({
        domain: SIWE_DOMAIN,
        address,
        uri: SIWE_URI,
        chainId,
        nonce,
        statement: "Sign in to THE GRID",
      });
      setStatus(t("auth.status.requestingSignature"));
      const signature = await signMessageAsync({ message });
      setStatus(t("auth.status.verifying"));
      const res = await api.verify(message, signature);
      setStatus(t("auth.status.signedIn", { wallet: res.wallet, path: res.path }));
      await refreshMe();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
      setStatus("");
    }
  }

  async function handleLogout() {
    await api.logout();
    setStatus(t("auth.status.signedOut"));
    await refreshMe();
  }

  return (
    <div
      data-testid="auth-route"
      className="w-full h-full overflow-y-auto flex items-start justify-center bg-obsidian py-10 px-4"
    >
      <div
        className={cn(
          "w-[min(560px,100%)] rounded-lg",
          "bg-panel border border-border-soft",
          "shadow-panel",
          "p-6 sm:p-8",
        )}
      >
        <h1 className="m-0 font-display text-[20px] font-semibold tracking-[-0.01em] text-text-primary">
          {t("auth.title")}
        </h1>
        <p className="mt-1.5 text-[13px] text-text-secondary">
          {t("auth.subtitle")}
        </p>

        <hr className="my-5 h-px border-0 bg-border-soft" />

        <section data-testid="wallet-section">
          <div className="text-[11px] uppercase tracking-[0.14em] text-text-muted">
            {t("auth.walletLabel")}
          </div>
          {isConnected ? (
            <div className="mt-2">
              <div
                data-testid="wallet-address"
                className="text-[13px] font-mono text-text-primary break-all"
              >
                {address}
              </div>
              <div className="mt-1 text-[12px] text-text-muted">
                {t("auth.chain", { name: activeChain?.name ?? "", id: activeChain?.id ?? "" })}
              </div>
            </div>
          ) : (
            <p className="mt-2 text-[13px] text-text-secondary">
              {t("auth.connectPrompt")}
            </p>
          )}
        </section>

        <section
          className="mt-6"
          data-testid="siwe-section"
        >
          <div className="text-[11px] uppercase tracking-[0.14em] text-text-muted">
            {t("auth.sessionLabel")}
          </div>
          {meLoading ? (
            <div className="mt-2 text-[13px] text-text-muted">{t("auth.loading")}</div>
          ) : me ? (
            <div className="mt-2 space-y-1">
              <div
                data-testid="me-wallet"
                className="text-[13px] font-mono text-text-primary"
              >
                {t("auth.signedInAs", { wallet: me.wallet })}
              </div>
              <div className="text-[12px] text-text-muted">
                {t("auth.expires", { date: new Date(me.expires_at).toLocaleString() })}
              </div>
              <button
                type="button"
                data-testid="logout-btn"
                onClick={handleLogout}
                className={cn(
                  "mt-3 h-9 px-4 rounded-md",
                  "text-[13px] font-medium",
                  "text-text-primary bg-transparent border border-border-soft",
                  "hover:bg-elevated transition-[background-color] duration-fast",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
                )}
              >
                {t("auth.signOut")}
              </button>
            </div>
          ) : (
            <div className="mt-3">
              <button
                type="button"
                data-testid="siwe-signin-btn"
                disabled={!isConnected}
                onClick={handleSignIn}
                className={cn(
                  "h-10 px-4 rounded-md",
                  "text-[13px] font-semibold",
                  "text-text-inverse bg-text-primary",
                  "hover:opacity-90 transition-opacity duration-fast",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {t("auth.signIn")}
              </button>
            </div>
          )}
        </section>

        {(status || error) && (
          <div className="mt-5" data-testid="status-panel">
            {status && (
              <div className="text-[13px] text-text-secondary">{status}</div>
            )}
            {error && (
              <div
                data-testid="error-message"
                className="mt-1 text-[13px] text-state-danger"
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
